import { Users } from 'lucide-react';
import React, { useState } from 'react';
import { EventLogUser } from '../../services/eventLog.service';
import Sparkline from './charts/Sparkline';

type Props = {
  users: EventLogUser[];
  loading?: boolean;
  selectedUserId?: string | null;
  onSelectUser: (userId: string | null) => void;
};

const COLLAPSED_LIMIT = 8;

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const activityRing = (relative: number): string => {
  // relative = 0..1 ratio of this user's activity vs the most active user
  if (relative >= 0.66) return 'ring-2 ring-green-400';
  if (relative >= 0.33) return 'ring-2 ring-amber-300';
  if (relative > 0) return 'ring-2 ring-gray-300';

  return 'ring-2 ring-gray-200';
};

const UsersStrip: React.FC<Props> = ({ users, loading, selectedUserId, onSelectUser }) => {
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          Users in this clinic
        </div>
        <div className="flex gap-3 overflow-x-auto pb-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-32 h-32 rounded-lg bg-gray-50 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!users.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          Users in this clinic
        </div>
        <p className="text-sm text-gray-500 py-4 text-center">
          No active users in the selected range
        </p>
      </div>
    );
  }

  const maxActivity = Math.max(...users.map(u => u.totalActivity), 1);
  const visible = showAll ? users : users.slice(0, COLLAPSED_LIMIT);
  const overflow = users.length - COLLAPSED_LIMIT;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          Users in this clinic
          <span className="ml-2 text-gray-400 font-normal normal-case">
            (click to filter the page)
          </span>
        </div>
        {selectedUserId && (
          <button
            type="button"
            onClick={() => onSelectUser(null)}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear filter
          </button>
        )}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1">
        {visible.map(user => {
          const isSelected = user.userId === selectedUserId;
          const relative = user.totalActivity / maxActivity;

          return (
            <button
              key={user.userId}
              type="button"
              onClick={() => onSelectUser(isSelected ? null : user.userId)}
              className={`flex-shrink-0 w-32 rounded-lg border p-3 text-left transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                isSelected
                  ? 'border-primary-400 bg-primary-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700 ${activityRing(
                    relative
                  )}`}
                >
                  {initials(user.userName)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">{user.userName}</div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 truncate">
                    {user.role || '—'}
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <Sparkline values={user.daily} width={104} height={20} />
              </div>
              <div className="mt-1 text-xs text-gray-500 tabular-nums">
                {user.totalActivity.toLocaleString()} events
              </div>
            </button>
          );
        })}

        {!showAll && overflow > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="flex-shrink-0 w-20 rounded-lg border border-dashed border-gray-300 text-xs text-gray-500 hover:bg-gray-50"
          >
            +{overflow} more
          </button>
        )}
      </div>
    </div>
  );
};

export default UsersStrip;
